import axios from "axios";
import { BACKEND_URL } from "../../../utils/api";

export interface ProfitEntry {
  id: string;
  date: string;
  heure: string | null;
  direction: "CAD_TO_XAF" | "XAF_TO_CAD";
  country: string;
  payment_method: string;
  capital_amount: number;
  capital_currency: string;
  buy_platform: string | null;
  buy_rate: number | null;
  buy_fees: number | null;
  usdt_obtained: number | null;
  sell_platform: string | null;
  sell_rate: number | null;
  sale_amount: number | null;
  izi_rate: number | null;
  izi_amount: number | null;
  profit_percentage: number | null;
  gross_profit: number | null;
  fees: number | null;
  net_profit: number | null;
  profit_cad: number | null;
  expense_amount: number | null;
  expense_currency: string | null;
  expense_description: string | null;
  final_profit: number | null;
  final_profit_cad: number | null;
  created_at: string;
  updated_at: string;
}

export interface CountrySummary {
  country: string;
  total_capital: number;
  total_gross_profit: number;
  total_fees: number;
  total_net_profit: number;
  total_profit_cad: number;
  total_expenses: number;
  total_final_profit: number;
  total_final_profit_cad: number;
  operation_count: number;
}

export interface PaymentMethodSummary {
  payment_method: string;
  country: string;
  total_capital: number;
  total_gross_profit: number;
  total_fees: number;
  total_net_profit: number;
  total_profit_cad: number;
  total_expenses: number;
  total_final_profit: number;
  total_final_profit_cad: number;
  operation_count: number;
}

export interface DailyProfit {
  date: string;
  payment_method: string;
  country: string;
  daily_capital: number;
  daily_gross_profit: number;
  daily_fees: number;
  daily_net_profit: number;
  daily_profit_cad: number;
  daily_expenses: number;
  daily_expenses_cad?: number;
  daily_final_profit: number;
  daily_final_profit_cad: number;
  operation_count: number;
}

export interface GlobalSummary {
  total_capital: number;
  total_gross_profit: number;
  total_fees: number;
  total_net_profit: number;
  total_profit_cad: number;
  total_expenses: number;
  total_final_profit: number;
  total_final_profit_cad: number;
  total_operations: number;
}

export interface ExpenseItem {
  id: string;
  profit_tracking_id: string;
  description: string;
  amount: number;
  currency: string;
  created_at: string;
  updated_at: string;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

interface ListParams {
  skip?: number;
  limit?: number;
  country?: string;
  payment_method?: string;
  direction?: string;
  date_from?: string;
  date_to?: string;
}

export const profitTrackingService = {
  async listEntries(params: ListParams = {}) {
    const res = await axios.get(`${BACKEND_URL}/profit-tracking/list`, {
      headers: getAuthHeaders(),
      params,
    });
    return res.data;
  },

  async createEntry(data: Partial<ProfitEntry>) {
    const res = await axios.post(`${BACKEND_URL}/profit-tracking/create`, data, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  async updateEntry(entryId: string, data: Partial<ProfitEntry>) {
    const res = await axios.put(`${BACKEND_URL}/profit-tracking/update/${entryId}`, data, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  async deleteEntry(entryId: string) {
    const res = await axios.delete(`${BACKEND_URL}/profit-tracking/delete/${entryId}`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  async recalculateAll() {
    const res = await axios.post(`${BACKEND_URL}/profit-tracking/recalculate`, {}, { headers: getAuthHeaders() });
    return res.data;
  },

  async summaryByCountry(params: { date_from?: string; date_to?: string; direction?: string } = {}) {
    console.log("params", params);

    try {
      const res = await axios.get(`${BACKEND_URL}/profit-tracking/summary/by-country`, {
        headers: getAuthHeaders(),
        params,
      });
      return res.data;
    } catch (error) {
      error;
    }
  },

  async summaryByPaymentMethod(
    params: { date_from?: string; date_to?: string; country?: string; direction?: string } = {}
  ) {
    console.log(params);
    try {
      const res = await axios.get(`${BACKEND_URL}/profit-tracking/summary/by-payment-method`, {
        headers: getAuthHeaders(),
        params,
      });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  async dailyProfitSummary(
    params: { date_from?: string; date_to?: string; country?: string; payment_method?: string; direction?: string } = {}
  ) {
    try {
      const res = await axios.get(`${BACKEND_URL}/profit-tracking/summary/daily`, {
        headers: getAuthHeaders(),
        params,
      });
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  async globalSummary(params: { date_from?: string; date_to?: string } = {}) {
    try {
      const res = await axios.get(`${BACKEND_URL}/profit-tracking/summary/global`, {
        headers: getAuthHeaders(),
        params,
      });
      console.log(res.data);
      return res.data;
    } catch (error) {
      throw error;
    }
  },

  async getCountries() {
    try {
      const res = await axios.get(`${BACKEND_URL}/profit-tracking/countries`, {
        headers: getAuthHeaders(),
      });

      return res.data;
    } catch (error) {
      throw error;
    }
  },

  async downloadEntriesExcel(params: {
    country?: string;
    payment_method?: string;
    direction?: string;
    date_from?: string;
    date_to?: string;
  }) {
    const res = await axios.get(`${BACKEND_URL}/profit-tracking/download/entries`, {
      headers: getAuthHeaders(),
      params,
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `profit_tracking_entries_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  },

  async downloadSummaryByCountryExcel(params: { date_from?: string; date_to?: string; direction?: string }) {
    const res = await axios.get(`${BACKEND_URL}/profit-tracking/download/summary/by-country`, {
      headers: getAuthHeaders(),
      params,
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `profit_summary_by_country_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  },

  async downloadSummaryByPaymentMethodExcel(params: {
    date_from?: string;
    date_to?: string;
    country?: string;
    direction?: string;
  }) {
    const res = await axios.get(`${BACKEND_URL}/profit-tracking/download/summary/by-payment-method`, {
      headers: getAuthHeaders(),
      params,
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `profit_summary_by_payment_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  },

  async downloadDailySummaryExcel(params: {
    date_from?: string;
    date_to?: string;
    country?: string;
    payment_method?: string;
    direction?: string;
  }) {
    const res = await axios.get(`${BACKEND_URL}/profit-tracking/download/summary/daily`, {
      headers: getAuthHeaders(),
      params,
      responseType: "blob",
    });
    const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `profit_daily_summary_${new Date().toISOString().slice(0, 10)}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
  },

  async createExpense(data: { profit_tracking_id: string; description: string; amount: number; currency?: string }) {
    const res = await axios.post(`${BACKEND_URL}/profit-tracking/expenses`, data, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  async listExpenses(profitTrackingId?: string) {
    const res = await axios.get(`${BACKEND_URL}/profit-tracking/expenses`, {
      headers: getAuthHeaders(),
      params: profitTrackingId ? { profit_tracking_id: profitTrackingId } : {},
    });
    return res.data;
  },

  async updateExpense(expenseId: string, data: Partial<ExpenseItem>) {
    const res = await axios.put(`${BACKEND_URL}/profit-tracking/expenses/${expenseId}`, data, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },

  async deleteExpense(expenseId: string) {
    const res = await axios.delete(`${BACKEND_URL}/profit-tracking/expenses/${expenseId}`, {
      headers: getAuthHeaders(),
    });
    return res.data;
  },
};
